"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Dialog } from "radix-ui";
import { ArrowUpRight, X } from "lucide-react";
import { BOOKING_EMBED_URL, BOOKING_INTRO, QUESTIONNAIRE_URL, closeBooking, useBookingOpen } from "@/lib/booking";
import { pauseScroll, resumeScroll } from "@/lib/lenis";

/**
 * Google's appointment schedule, framed full screen over the page. Mounted once in the
 * layout and opened by any BookCall. Like ClientSheet, Radix owns focus, Escape, the
 * scroll lock and the inert page, Framer owns the motion, and Lenis is paused while it is up.
 *
 * The frame is only created on open, so Google's page costs nothing until someone asks
 * for it. Google clamps the schedule's description behind "Show more" at every frame size,
 * so the intro bar above it says why the pre-call questions are worth
 * answering and links the optional questionnaire.
 */
export const BookingDialog = () => {
  const open = useBookingOpen();
  const reduce = useReducedMotion();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open) return;
    pauseScroll();
    return () => {
      resumeScroll();
      setLoaded(false);
    };
  }, [open]);

  if (!BOOKING_EMBED_URL) return null;

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && closeBooking()}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild forceMount>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: reduce ? 0 : 0.25 }}
                className="fixed inset-0 z-[70] bg-brand-navy"
              />
            </Dialog.Overlay>
            <Dialog.Content asChild forceMount>
              <motion.div
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24 }}
                animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, y: 24 }}
                transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 300, damping: 34, mass: 0.9 }}
                className="fixed inset-0 z-[71] flex flex-col bg-white outline-none"
              >
                <div className="flex shrink-0 items-start gap-4 bg-brand-navy px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] text-white sm:px-6 sm:pb-4 sm:pt-4 lg:items-center lg:gap-8 lg:px-8 lg:py-5">
                  <div className="min-w-0 flex-1 lg:flex lg:items-center lg:gap-10">
                    <div className="shrink-0">
                      <p className="hidden font-mono text-[10px] uppercase tracking-[0.22em] text-brand-mint sm:block">
                        30-minute call · about 2 minutes of questions
                      </p>
                      <Dialog.Title className="font-heading text-[1.35rem] uppercase leading-[0.92] tracking-tight sm:mt-2 sm:text-[1.7rem]">
                        {BOOKING_INTRO.lead}
                      </Dialog.Title>
                    </div>
                    <div className="mt-2 max-w-2xl lg:mt-0">
                      <Dialog.Description className="text-[12.5px] leading-snug text-white/75 sm:text-sm lg:leading-relaxed">
                        {BOOKING_INTRO.body}
                      </Dialog.Description>
                      <a
                        href={QUESTIONNAIRE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-mint underline decoration-brand-mint/50 underline-offset-4 outline-none transition-colors duration-300 hover:text-white focus-visible:ring-2 focus-visible:ring-brand-mint"
                      >
                        Answer the questions (optional)
                        <ArrowUpRight className="size-3" />
                      </a>
                    </div>
                  </div>
                  <Dialog.Close
                    aria-label="Close booking"
                    className="grid size-10 shrink-0 place-items-center rounded-btn bg-white text-brand-ink outline-none transition-colors duration-300 hover:bg-brand-mint focus-visible:ring-2 focus-visible:ring-brand-mint"
                  >
                    <X className="size-5" />
                  </Dialog.Close>
                </div>

                <div className="relative min-h-0 flex-1 bg-white">
                  {!loaded && (
                    <div aria-hidden className="absolute inset-0 grid place-items-center">
                      <span className="size-8 animate-spin rounded-full border-2 border-brand-ink/15 border-t-brand-accent" />
                    </div>
                  )}
                  <iframe
                    src={BOOKING_EMBED_URL}
                    title="Book a 30-minute call with JRAT'S Studio"
                    onLoad={() => setLoaded(true)}
                    className="relative size-full border-0"
                  />
                </div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
};
